<template>
  <SummarizationContainerCommon
    :loading="loading"
    :is-transcription-result-empty="isTranscriptionResultEmpty"
  >
    <SummarizationHeaderCommon
      :created-at="summarization?.createdAt"
      :is-refetch-button-visible="!summarization"
      :recreate-button-tooltip-message="recreateButtonTooltipMessage"
      :can-recreate="canRecreate"
      :loading="loading"
      @click:recreate="handleRecreateClick"
      @click:refetch="fetchSummarization"
    />
    <SummarizationContentCommon
      v-if="!!summarization"
      :content="summarization?.summarizedResult"
      @click:play-at="handlePlayAtClick"
    />
    <SummarizationErrorBodyCommon
      v-else
      :resource-name="i18n.t('transcription.summarization')"
    />
  </SummarizationContainerCommon>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { TranscriptionSummarization } from '@/api/openapi';
import { TranscriptionSummarizationApiService } from '@/api/user/resources/transcription_summarization';
import { useConfirm, useAlert } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import SummarizationContainerCommon from './SummarizationContainerCommon.vue';
import SummarizationContentCommon from './SummarizationContentCommon.vue';
import SummarizationHeaderCommon from './SummarizationHeaderCommon.vue';

type TProps = {
  zoomPhoneResultId: number | null;
  isTranscriptionResultEmpty: boolean;
};
type TEmits = {
  'click:playAt': [time: number];
  'update:summarizedResult': [summarizedResult: string | undefined];
}
const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();

const summarization = ref<TranscriptionSummarization | null>(null);

const { doActionWithWait, wait } = useWait();
const i18n = useI18n();
const { confirm } = useConfirm();
const { alert } = useAlert();

const summarizableCount = computed(() => {
  return summarization.value?.summarizableCount || 0;
});

const maxSummarizableCount = computed(() => {
  return summarization.value?.maxSummarizableCount;
});

const loading = computed(() => {
  return wait.is(['fetchSummarizationWait', 'summarizeTranscriptionWait']);
});

const canRecreate = computed(() => {
  return summarizableCount.value > 0;
});

const recreateButtonTooltipMessage = computed(() => {
  if (canRecreate.value) {
    return i18n.t('transcription.recreatableCount', { recreatableCount: summarizableCount.value });
  }
  return i18n.t('transcription.cannotRecreateBecauseTryCountLimitReached', { maxTryCount: maxSummarizableCount.value });
});

const fetchSummarization = async () => {
  if (props.isTranscriptionResultEmpty) return;
  doActionWithWait('fetchSummarizationWait', async () => {
    const api = new TranscriptionSummarizationApiService();
    const { data: { transcriptionSummarization } } = await api.getTranscriptionSummarization({
      request: {
        zoomPhoneResultId: props.zoomPhoneResultId,
      },
      errorHandlers: {
        404: () => {
          summarization.value = null;
        },
      },
    });
    summarization.value = transcriptionSummarization;
  });
};

const summarizeTranscriptionErrorHandler = () => {
  alert({
    title: i18n.t('transcription.failedToRecreate.title', { resourceName: i18n.t('transcription.summarization') }),
    message: i18n.t('transcription.failedToRecreate.message', { resourceName: i18n.t('transcription.summarization') }),
  });
};

const summarizeTranscription = async () => {
  doActionWithWait('summarizeTranscriptionWait', async () => {
    const api = new TranscriptionSummarizationApiService();
    await api.postTranscriptionSummarize({
      request: { zoomPhoneResultId: props.zoomPhoneResultId },
      errorHandlers: {
        500: summarizeTranscriptionErrorHandler,
        422: summarizeTranscriptionErrorHandler,
      },
    });
    await fetchSummarization();
  });
};

watch(() => summarization.value?.summarizedResult, (newValue) => {
  emit('update:summarizedResult', newValue);
}, { immediate: true });

const handleRecreateClick = async () => {
  const agreed = await confirm({
    title: i18n.t('transcription.confirmRecreateSummarization.title'),
    message: i18n.t('transcription.confirmRecreateSummarization.message', {
      recreatableCount: summarizableCount.value,
      maxTryCount: maxSummarizableCount.value,
    }),
  });
  if (agreed) {
    summarizeTranscription();
  }
};

const handlePlayAtClick = (time: number) => {
  emit('click:playAt', time);
};

onBeforeMount(async () => {
  await fetchSummarization();
});
</script>

